import {
  Body1,
  Caption1,
  makeStyles,
  mergeClasses,
  Title3,
  tokens,
} from "@fluentui/react-components";
import type { ReactNode } from "react";
import { baeTypography } from "../theme/baeTheme";
import { motion, PHONE } from "./tokens";

const useStyles = makeStyles({
  header: {
    display: "flex",
    alignItems: "flex-start",
    gap: tokens.spacingHorizontalM,
    flexWrap: "wrap",
    minWidth: 0,
  },
  text: { display: "grid", gap: "2px", flexGrow: 1, minWidth: "12rem" },
  title: {
    fontFamily: baeTypography.fontFamilyDisplay,
    letterSpacing: "-0.025em",
    lineHeight: 1.15,
    margin: 0,
  },
  muted: { color: tokens.colorNeutralForeground3, maxWidth: "44rem" },
  actions: {
    display: "flex",
    gap: tokens.spacingHorizontalS,
    flexWrap: "wrap",
    alignItems: "center",
  },

  page: {
    display: "grid",
    gap: tokens.spacingVerticalL,
    alignContent: "start",
    minWidth: 0,
    // Content entering on a tab change, rather than snapping in. Short enough that it never reads
    // as a wait; the global reduced-motion rule turns it off for anyone who asked.
    animationName: {
      from: { opacity: 0, transform: "translateY(4px)" },
      to: { opacity: 1, transform: "none" },
    },
    animationDuration: motion.enter,
  },
  narrow: { maxWidth: "36rem", marginInline: "auto", width: "100%" },
  medium: { maxWidth: "58rem" },

  // A row of controls that belongs to the content below it rather than to the page.
  //
  // 9.5rem rather than 11: at 11 a 375px phone fits exactly one per line, which turns four filters
  // into four full-height rows and pushes the content itself off the screen. At 9.5 the short ones
  // pair up, and anything that needs the full width asks for it.
  toolbar: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(9.5rem, 1fr))",
    gap: tokens.spacingHorizontalM,
    alignItems: "end",
    minWidth: 0,
  },
  empty: {
    display: "grid",
    justifyItems: "center",
    gap: tokens.spacingVerticalS,
    padding: `${tokens.spacingVerticalXXXL} ${tokens.spacingHorizontalL}`,
    textAlign: "center",
    color: tokens.colorNeutralForeground3,
    [PHONE]: { padding: `${tokens.spacingVerticalXXL} ${tokens.spacingHorizontalM}` },
  },
  emptyIcon: {
    display: "grid",
    placeItems: "center",
    width: "3rem",
    height: "3rem",
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground3,
    fontSize: "24px",
  },
  emptyTitle: { color: tokens.colorNeutralForeground1 },
});

/** A page's wrapper: consistent gaps, a consistent width, and one entrance. */
export function Page({
  children,
  width = "wide",
  className,
}: {
  children: ReactNode;
  /** `narrow` for a single-column form, `medium` for a list, `wide` for a dashboard. */
  width?: "narrow" | "medium" | "wide";
  className?: string;
}) {
  const styles = useStyles();

  return (
    <div
      className={mergeClasses(
        styles.page,
        width === "narrow" && styles.narrow,
        width === "medium" && styles.medium,
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * The title block at the top of a page.
 *
 * The subtitle is not decoration. Every screen here does something with real consequences —
 * receives stock, deletes a category — and one line saying what it is for is the difference
 * between a confident click and a cautious one.
 */
export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  const styles = useStyles();

  return (
    <header className={styles.header}>
      <div className={styles.text}>
        <Title3 as="h2" className={styles.title}>
          {title}
        </Title3>
        {subtitle && <Caption1 className={styles.muted}>{subtitle}</Caption1>}
      </div>
      {actions && <div className={styles.actions}>{actions}</div>}
    </header>
  );
}

/** A row of filters or fields that reflows to a stack when it stops fitting. */
export function Toolbar({ children }: { children: ReactNode }) {
  const styles = useStyles();
  return <div className={styles.toolbar}>{children}</div>;
}

/**
 * What a screen says when there is nothing on it yet.
 *
 * A line of grey text was what this used to be, and an empty screen is the first thing a new
 * user sees — so it is the one place worth saying what to do next rather than only what is
 * absent.
 */
export function EmptyState({
  icon,
  title,
  children,
  action,
}: {
  icon: ReactNode;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  const styles = useStyles();

  return (
    <div className={styles.empty}>
      <span className={styles.emptyIcon}>{icon}</span>
      <Body1 className={styles.emptyTitle}>{title}</Body1>
      {children && <Caption1>{children}</Caption1>}
      {action}
    </div>
  );
}
